import * as React from "react";
import { observer } from "mobx-react";

import {
  UitkExperimentalButtonTabs,
  UitkTab,
  UitkTabsType,
  UitkTabAnchorType,
} from "uitk-react-experimental-button-tabs";
import { liveAnnounce, Level } from "uitk-react-utils";
import { UitkLayoutConditionalGridSpan } from "uitk-react-layout-grid";
import { UitkLayoutFlex, UitkLayoutFlexItem } from "uitk-react-layout-flex";
import { UitkSpacing } from "uitk-react-spacing";

import { useLocalization } from "@shared-ui/localization-context";
import { Viewport, ViewSmall, ViewLarge } from "@shared-ui/viewport-context";

import { LobTitle } from "components/shared/LobTitle/LobTitle";
import { SubmitRow } from "components/shared/StorefrontWizard/SubmitRow/SubmitRow";
import { Travelers } from "components/shared/Travelers/Travelers";
import { PreferredClassInput } from "components/shared/PreferredClassInput/PreferredClassInput";
import { PreferredAirlineInput } from "components/shared/PreferredAirlineInput/PreferredAirlineInput";
import { TravelersTriggerTypes } from "components/shared/TravelersField/typings";
import { FlightType } from "stores/wizard/state/typings";
import { LobSubtitle } from "components/shared/LobSubtitle/LobSubtitle";

import { FlightHiddenSearchDataProps, WizardFlightPWAViewProps } from "./typings";
import { RoundtripFlight } from "./components/RoundtripFlight";
import { OnewayFlight } from "./components/OnewayFlight";
import { MulticityFlight } from "./components/MulticityFlight";

import { FlightHiddenSearchData } from "./components/FlightHiddenSearchData";
import { WizardFlightPWA } from "./WizardFlightPWA";
import { FHCHiddenSearchData } from "./components/FHCHiddenSearchData";

import { locKeys } from "./components/l10n";
import { Columns } from "components/shared/StorefrontWizard/typings";

export const WizardFlightPWAView: React.FunctionComponent<WizardFlightPWAViewProps> = observer((props) => {
  const {
    globalWizardState,
    flightWizardState,
    flightWizardState: { config: flightConfig },
  } = props.wizardState;

  const { formatText } = useLocalization();

  const tabIdPrefix = "wizard-flight-tab-";
  const { flightClassCode, listOfAdditionalLegs, subLOBState, wizardInputsArray } = flightWizardState;
  const currentTab = subLOBState; // tab state is calculated based on subLOB

  if (listOfAdditionalLegs.length > 1) {
    liveAnnounce(formatText(locKeys.multiCityFlightLegLabel, listOfAdditionalLegs.length + 1), Level.POLITE);
  }

  const flightHiddenSearchDataProps: FlightHiddenSearchDataProps = {
    flightType: "on",
    flightClass: WizardFlightPWA.getFlightClassCodeForURL(flightClassCode),
    flightAirline: globalWizardState.flightAirline,
    mode: "search",
    trip: subLOBState,
    leg1: WizardFlightPWA.getLegURLFragment(flightWizardState, flightConfig)[0],
    leg2: WizardFlightPWA.getLegURLFragment(flightWizardState, flightConfig)[1],
    flightWizardState: props.wizardState.flightWizardState,
    globalWizardState,
  };

  const { halfWizard } = globalWizardState.config;
  const travelersColumns: Columns = {
    fullWidth: { small: 2, medium: 2, large: 2 },
    half: {
      small: 2,
      large: 12,
    },
  };
  const preferredClassColumn: Columns = {
    fullWidth: { small: 3, medium: 2, large: 2 },
    half: {
      small: 1,
      large: 4,
    },
  };

  const setInputsRefs = (ref: React.RefObject<HTMLInputElement>, secondRef?: React.RefObject<HTMLInputElement>) => {
    wizardInputsArray.push(ref);
    if (secondRef) {
      wizardInputsArray.push(secondRef);
    }
  };

  const getLocError = (isLink: boolean) => {
    if (!isLink) {
      return flightWizardState.moreThanOneError()
        ? flightConfig.form.submitButton.locMultipleWarningsToken!
        : flightConfig.form.submitButton.locWarningToken!;
    }

    return flightWizardState.moreThanOneError()
      ? flightConfig.form.submitButton.locMultipleWarningsLinkToken
      : flightConfig.form.submitButton.locWarningLinkToken;
  };
  const travelersColSpan: UitkLayoutConditionalGridSpan = travelersColumns[halfWizard ? "half" : "fullWidth"];
  const preferredClassColSpan: UitkLayoutConditionalGridSpan = preferredClassColumn[halfWizard ? "half" : "fullWidth"];
  const { config, isAddAHotelChecked, isAddACarChecked } = flightWizardState;
  const travelerConfig =
    config.enableAddHotel && isAddAHotelChecked
      ? config.travelersWithHotel
      : config.enableAddCar && isAddACarChecked
      ? config.travelersWithCar
      : config.travelers;

  /**
   * Value used if no rehydratation(JS disabled)
   */
  const { noRehydratationValues } = flightConfig.travelers;

  /**
   * Set the new tab to display and update global state
   */
  const handleTabChange = (flightType: FlightType) => {
    props.onSelectFlightType(flightType);
  };

  /**
   * Builds the Travelers and PreferredClassInput
   * as a prop to be sent to UitkExperimentalButtonTabs
   * to display them next to the UitkTabs, at the right
   */
  const noNavigationItems = () => (
    <Viewport>
      <ViewLarge>
        <UitkLayoutFlex alignItems="center" space="six" className="all-x-padding-six">
          <UitkLayoutFlexItem>
            <Travelers
              lobState={flightWizardState}
              config={travelerConfig}
              colSpan={travelersColSpan}
              noRehydratationValues={noRehydratationValues}
              triggerType={TravelersTriggerTypes.LINK_BUTTON}
            />
          </UitkLayoutFlexItem>
          <UitkLayoutFlexItem>
            <PreferredClassInput
              colSpan={preferredClassColSpan}
              onSelectPreferredClassCode={props.onSelectPreferredClassCode}
              wizardState={props.wizardState}
            />
          </UitkLayoutFlexItem>
          <UitkLayoutFlexItem>
            <PreferredAirlineInput
              onSelectPreferredAirline={props.onSelectPreferredAirline}
              wizardState={props.wizardState}
            />
          </UitkLayoutFlexItem>
        </UitkLayoutFlex>
      </ViewLarge>
    </Viewport>
  );

  const referrerId =
    currentTab !== FlightType.MULTI_CITY ? flightConfig.referrerId : flightConfig.multiCityFlight.referrerId;

  const { pageHeading, pageSubHeadline } = props.compositionStore.composition!;

  const titleFromComposition = (props.flexComponent.model.titleFromPageHeader && pageHeading) || undefined;
  const subTitleFromComposition = (props.flexComponent.model.subTitleFromPageSubHeader && pageSubHeadline) || undefined;

  return (
    <form
      noValidate={true}
      className="WizardFlightPWA"
      action={
        flightWizardState.isAddAHotelChecked || flightWizardState.isAddACarChecked
          ? "/flexibleshopping"
          : "/Flights-Search"
      }
      onSubmit={flightWizardState.submit}
      id={flightConfig.elementId}
      data-fm={props.flexComponent.fmId}
      data-fm-title-id={props.flexComponent.fmTitleId}
    >
      {flightWizardState.isAddAHotelChecked || flightWizardState.isAddACarChecked ? (
        <FHCHiddenSearchData flightWizardState={props.wizardState.flightWizardState} />
      ) : (
        <FlightHiddenSearchData {...flightHiddenSearchDataProps} />
      )}

      <LobTitle lobState={flightWizardState} customTitle={titleFromComposition} />
      <LobSubtitle lobState={flightWizardState} customSubTitle={subTitleFromComposition} />
      {/* Each tab represents a flights sub LOB (roundtrip, one way and multicity) */}
      <UitkSpacing margin={{ blockstart: "three" }}>
        <UitkExperimentalButtonTabs
          className="flightTypeTabs"
          selectedTab={currentTab}
          onTabSelect={handleTabChange}
          tabsType={UitkTabsType.NATURAL}
          tabAnchorType={UitkTabAnchorType.BUTTON}
          noNavigationItems={halfWizard ? undefined : noNavigationItems}
          data-testid={currentTab}
        >
          <UitkTab
            name={FlightType.ROUND_TRIP}
            displayName={formatText(locKeys.flightRoundTripText)}
            targetURI={`?flightType=${FlightType.ROUND_TRIP}`}
            id={`${tabIdPrefix}${FlightType.ROUND_TRIP}`}
          >
            {currentTab === FlightType.ROUND_TRIP && (
              <RoundtripFlight
                setInputsRefs={setInputsRefs}
                lobState={flightWizardState}
                flightConfig={flightConfig}
                getLocError={getLocError}
                {...props}
              />
            )}
          </UitkTab>

          <UitkTab
            name={FlightType.ONE_WAY}
            displayName={formatText(locKeys.flightOneWayText)}
            targetURI={`?flightType=${FlightType.ONE_WAY}`}
            id={`${tabIdPrefix}${FlightType.ONE_WAY}`}
          >
            {currentTab === FlightType.ONE_WAY && (
              <OnewayFlight
                setInputsRefs={setInputsRefs}
                lobState={flightWizardState}
                flightConfig={flightConfig}
                getLocError={getLocError}
                {...props}
              />
            )}
          </UitkTab>

          <UitkTab
            name={FlightType.MULTI_CITY}
            displayName={formatText(locKeys.flightMultiCityText)}
            targetURI={`?flightType=${FlightType.MULTI_CITY}`}
            id={`${tabIdPrefix}${FlightType.MULTI_CITY}`}
          >
            {currentTab === FlightType.MULTI_CITY && (
              <MulticityFlight
                setInputsRefs={setInputsRefs}
                lobState={flightWizardState}
                flightConfig={flightConfig}
                getLocError={getLocError}
                {...props}
              />
            )}
          </UitkTab>
        </UitkExperimentalButtonTabs>
      </UitkSpacing>
      <Viewport>
        {!halfWizard ? (
          <ViewSmall>
            <UitkLayoutFlex alignItems="center" space="six" className="all-t-padding-four">
              <UitkLayoutFlexItem>
                <div>
                  <Travelers
                    lobState={flightWizardState}
                    config={travelerConfig}
                    colSpan={travelersColSpan}
                    noRehydratationValues={noRehydratationValues}
                    triggerType={TravelersTriggerTypes.LINK_BUTTON}
                  />
                </div>
              </UitkLayoutFlexItem>
              <UitkLayoutFlexItem>
                <PreferredClassInput
                  colSpan={preferredClassColSpan}
                  onSelectPreferredClassCode={props.onSelectPreferredClassCode}
                  wizardState={props.wizardState}
                />
              </UitkLayoutFlexItem>
              <UitkLayoutFlexItem>
                <PreferredAirlineInput
                  onSelectPreferredAirline={props.onSelectPreferredAirline}
                  wizardState={props.wizardState}
                />
              </UitkLayoutFlexItem>
            </UitkLayoutFlex>
          </ViewSmall>
        ) : (
          <ViewSmall />
        )}
        <ViewLarge />
      </Viewport>
      <SubmitRow lobState={flightWizardState} rfrr={referrerId} globalConfig={globalWizardState.config} />
    </form>
  );
});
